import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, PackageCheck, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDateTime } from "../../lib/api";
import { PageHeader, StatusBadge, LoadingRows, EmptyState } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export default function FreelancerOrders() {
  const [orders, setOrders] = useState(null);
  const [deliverFor, setDeliverFor] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/mkt/freelancer/orders").then((r) => setOrders(r.data.orders)).catch((e) => { toast.error(errMsg(e)); setOrders([]); });
  useEffect(() => { load(); }, []);

  const deliver = async () => {
    setBusy(true);
    try {
      await api.post(`/mkt/freelancer/orders/${deliverFor.id}/deliver`, { note });
      toast.success("Marked as delivered — buyer will review"); setDeliverFor(null); setNote(""); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2" data-testid="freelancer-orders-page">
      <PageHeader title="Marketplace Orders" subtitle="Deliver work and grow your ratings" testid="fl-orders-title" />
      {!orders ? <LoadingRows rows={4} /> : orders.length === 0 ? (
        <EmptyState title="No orders yet" body="Publish listings and share your profile to get your first order." />
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} data-testid={`fl-order-${o.id}`} className="bg-card border border-border rounded-xl p-5 flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-display font-bold text-sm">{o.item_title}</p>
                  <StatusBadge status={o.status} />
                  <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">{o.kind}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Buyer: <b>{o.buyer_name}</b> · {fmtDateTime(o.created_at)}{o.package_desc ? ` · ${o.package_desc}` : ""}</p>
                {o.requirements && <p className="text-xs mt-2 px-3 py-2 rounded-lg bg-muted text-muted-foreground"><b>Requirements:</b> {o.requirements}</p>}
                {o.delivery_note && <p className="text-xs mt-2 px-3 py-2 rounded-lg bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"><b>Delivered:</b> {o.delivery_note}</p>}
              </div>
              <div className="text-right">
                <p className="font-display font-extrabold text-lg">{fmtINR(o.freelancer_amount)}</p>
                <p className="text-[10px] text-muted-foreground">of {fmtINR(o.amount)} · #{o.id.slice(0, 8)}</p>
              </div>
              <div className="flex gap-2">
                {o.status === "active" && (
                  <Button size="sm" data-testid={`fl-order-deliver-${o.id}`} onClick={() => setDeliverFor(o)} className="rounded-full font-bold gradient-bg text-white border-0">
                    <PackageCheck className="h-3.5 w-3.5 mr-1" />Deliver Work</Button>)}
                {["active", "delivered", "completed"].includes(o.status) && (
                  <Button size="sm" variant="outline" data-testid={`fl-order-chat-${o.id}`} onClick={() => navigate(`/mkt/messages/${o.buyer_id}`)} className="rounded-full font-bold">
                    <MessageSquare className="h-3.5 w-3.5 mr-1" />Chat</Button>)}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!deliverFor} onOpenChange={(o) => !o && setDeliverFor(null)}>
        <DialogContent data-testid="deliver-dialog">
          <DialogHeader><DialogTitle className="font-display">Deliver Order</DialogTitle></DialogHeader>
          {deliverFor && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground"><b>{deliverFor.item_title}</b> for {deliverFor.buyer_name}</p>
              <div className="space-y-1.5">
                <Label>Delivery note (links, drive folders, instructions)</Label>
                <Textarea data-testid="deliver-note" value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="Paste delivery links and notes for the buyer…" />
              </div>
              <Button data-testid="deliver-submit" onClick={deliver} disabled={busy} className="w-full rounded-full font-bold gradient-bg text-white border-0">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mark as Delivered"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
